const LAMPORTS_PER_SOL = 1_000_000_000;

/** Buffer for signature + compute fees on a claim/unlock tx. */
export const CLAIM_TX_FEE_BUFFER_LAMPORTS = 2_000_000;

/** Typical SPL token-account rent; padded in claimSolNeededLamports for Token-2022. */
export const DEFAULT_ATA_RENT_LAMPORTS = 2_039_280;

const NOT_ENOUGH_SOL_MESSAGE =
  "This wallet does not have enough SOL to pay the network fee (and token-account rent if you have not received this token before). Send SOL to this wallet, then try again.";

export function formatSolLamports(lamports) {
  const n = Number(lamports) / LAMPORTS_PER_SOL;
  if (!Number.isFinite(n) || n <= 0) return "0";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

export function claimSolNeededLamports({
  needsAta = false,
  ataRentLamports = DEFAULT_ATA_RENT_LAMPORTS,
} = {}) {
  const rent = needsAta ? Number(ataRentLamports) || DEFAULT_ATA_RENT_LAMPORTS : 0;
  return CLAIM_TX_FEE_BUFFER_LAMPORTS + rent;
}

export function lowSolClaimMessage({
  solLamports = 0,
  neededLamports = CLAIM_TX_FEE_BUFFER_LAMPORTS,
  needsAta = false,
  role = "wallet",
} = {}) {
  const have = formatSolLamports(solLamports);
  const need = formatSolLamports(neededLamports);
  const who = role === "receiver" ? "This receiver wallet" : "This wallet";
  const rentBit = needsAta
    ? "creating a token account and network fees"
    : "network fees";
  if (!solLamports || solLamports <= 0) {
    return `${who} has 0 SOL. Claiming needs about ${need} SOL for ${rentBit}. Send SOL to this wallet, then claim.`;
  }
  return `${who} only has ${have} SOL. Claiming needs about ${need} SOL${
    needsAta ? " (new token account rent + network fee)" : " for network fees"
  }. Send more SOL to this wallet, then try again.`;
}

function looksLikeSolShortage(blob) {
  return (
    /insufficient lamports/i.test(blob) ||
    /insufficient funds for (rent|fee|sol)/i.test(blob) ||
    /not enough sol/i.test(blob) ||
    /no record of a prior credit/i.test(blob) ||
    /Attempt to debit an account/i.test(blob) ||
    /insufficient lamport/i.test(blob)
  );
}

/** Anchor custom errors for liquidity_locker (`#[error_code]` starts at 6000). */
export const LOCKER_CUSTOM_ERRORS = {
  6000: "Amount must be greater than zero.",
  6001: "Unlock time must be in the future.",
  6002: "Tokens are still locked.",
  6003: "Signer is not the lock owner.",
  6004: "Token mint does not match this lock.",
  6005: "Vault token account is invalid.",
  6006: "Locked amount overflow.",
  6007: "Target price must be greater than zero.",
  6008: "Base and quote mints must differ.",
  6009: "Use the matching unlock path for this lock type.",
  6010: "Pool vault mint does not match this lock’s quote.",
  6011: "Pool vault is empty.",
  6012:
    "The pool price is still below your unlock target. Tokens stay locked until spot reaches the target (or the expiry, if you set one).",
  6013: "Cliff schedule is invalid.",
  6014: "Too many cliff periods.",
  6015: "No newly vested tokens to claim.",
  6016: "Cliff schedule is required for this lock.",
  6017: "Receiver list is invalid.",
  6018: "Receivers are already set for this lock.",
  6019: "This lock pays receivers — they must claim their share.",
  6020: "Receiver schedule not found.",
};

function parseCustomCode(blob) {
  const text = String(blob || "");
  const named = text.match(/Error Number:\s*(\d{4,5})/);
  if (named) return Number(named[1]);
  const custom = text.match(/Custom["\s:]+(\d{4,5})/);
  if (custom) return Number(custom[1]);
  const hex = text.match(/custom program error:\s*0x([0-9a-f]+)/i);
  if (hex) return parseInt(hex[1], 16);
  return null;
}

/**
 * @param {unknown} err
 * @param {string[]} [logs]
 * @param {{ preferSolShortage?: boolean }} [opts]
 * @returns {string | null}
 */
export function explainLockerFailure(err, logs = [], opts = {}) {
  const blob = `${typeof err === "string" ? err : JSON.stringify(err || {})}\n${(logs || []).join("\n")}`;
  const code = parseCustomCode(blob);
  if (code != null && LOCKER_CUSTOM_ERRORS[code]) return LOCKER_CUSTOM_ERRORS[code];
  if (/PriceNotMet|Spot price has not reached/i.test(blob)) {
    return LOCKER_CUSTOM_ERRORS[6012];
  }
  if (/AccountOwnedByWrongProgram|AccountNotInitialized/i.test(blob)) {
    return "This lock transaction was built with the wrong accounts. Refresh the page and try again.";
  }
  if (looksLikeSolShortage(blob) || (opts.preferSolShortage && /insufficient funds/i.test(blob))) {
    return NOT_ENOUGH_SOL_MESSAGE;
  }
  if (/insufficient funds/i.test(blob) || code === 1) {
    if (opts.preferSolShortage) return NOT_ENOUGH_SOL_MESSAGE;
    return "Not enough tokens in your wallet for this amount. If you already locked the full balance, there is nothing left to add.";
  }
  return null;
}
